import config from "appConfig";

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>
   A geophone is a sensor which is used for measuring the velocity of the ground in order to, for example, record seismic vibrations or analyze the structure of the earth. It is therefore an mechano-electrical transducer. Passive geophones usually use a moving coil conversion technology with a voltage sensitivity expressed in V
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>⋅</mo>
   </math>
   s/m. This Lesson is dedicated to the modelling of this type of transducer [1].
<h3 class=sectionHead>Working principle</h3>
<p>
   A schematic diagram of a moving coil geophone is represented in <a href="#" data-img-src=${config.ASSETS_URL}/04/13/theory/figure-1.svg data-img-alt="Figure 1. Moving coil geophone (cut view).">Figure 1</a>. A cylindrical coil is attached to the casing by two suspensions. A cylindrical permanent magnet is placed in the coil creating a radial magnetic field. When the floor vibrates vertically, the housing and the coil also vibrate, but at a different velocity because they are connected by a non-rigid element: the suspensions. As the coil moves perpendicular to the magnetic field, an induced EMF is created at its terminals. If the coil is connected to an electrical load, a current will flow through it generating a Lorentz force which will partially oppose the movement.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/13/theory/figure-1.svg alt="Figure 1. Moving coil geophone (cut view)."><figcaption class=caption>Figure 1. Moving coil geophone (cut view).</figcaption></figure>
<h3 class=sectionHead>Behavioural equation</h3>
<p>Let us assume that at low frequency, the electro-mechanical behaviour of the geophone can be modelled by using the following elements:
<ul class=itemize1>
   <li class=itemize>
      the coil has a length
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>ℓ</mi>
      </math>
      and a mass
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      the two suspensions have an equivalent compliance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>
      and a mechanical resistance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      the casing or box of mass
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>
      is vibrating with a velocity
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>
      while the coil is vibrating with a velocity
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>V</mi></math>,
   <li class=itemize>
      the magnet is rigidly attached to the casing and produces a radial magnetic field
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>B</mi>
      </math>,
   <li class=itemize>
      the current flowing in the coil of impedance
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mi mathvariant=italic>jω</mi>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>
      is denoted
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>I</mi>
      </math>,
   <li class=itemize>
      the coil is connected to a resistor
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
      </math>
      at the terminals of which the geophone output voltage is measured.
</ul>
<p>
   The mechanical network representing the geophone behaviour is shown in <a href="#" data-img-src=${config.ASSETS_URL}/04/13/theory/figure-2.svg data-img-alt="Figure 2. Mechanical system representing the geophone behaviour.">Figure 2</a>, it corresponds to a 2 Degrees Of Freedom (2-DOF) system.
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/13/theory/figure-2.svg alt="Figure 2. Mechanical system representing the geophone behaviour.">
      <figcaption class=caption>Figure 2. Mechanical system representing the geophone behaviour.</figcaption>
   </figure>
<p>
   Now, let us write the equations governing the coil behaviour, starting with the second law of Newton for a harmonic motion of pulsation
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ω</mi>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mo class=MathClass-bin>−</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-open>(</mo>
            <mi>V</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>V</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>BℓI</mi>
   </math>
   is the Lorentz force due to the electrodynamic coupling. Please note that the restoring force of the suspension and the friction force are proportional to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <mi>V</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   the relative velocity of the coil to the casing one.<br class=newline>The electrical equation is given by
<table class=equation>
   <tr>
      <td style="font-size: 15px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>e</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>V</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mi>I</mi>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>e</mi>
   </math>, the EMF caused by the coil vibration, is proportional to the relative velocity of the coil to the magnet one. Using the electromechanical analogies and a gyrator for the electrodynamic coupling, the behavioural equations can be expressed by the circuit depicted in <a href="#" data-img-src=${config.ASSETS_URL}/04/13/theory/figure-3.svg data-img-alt="Figure 3. Equivalent electrical circuit representing the geophone behaviour.">Figure 3</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/13/theory/figure-3.svg alt="Figure 3. Equivalent electrical circuit representing the geophone behaviour."><figcaption class=caption>Figure 3. Equivalent electrical circuit representing the geophone behaviour.</figcaption></figure>
<h3 class=sectionHead>Sensitivity</h3>
<p>
   The sensitivity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
   </math>
   of the geophone is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>o</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>. It can be obtained from the behavioural equations (1) and (2). For most electrodynamic transducers, the inductance is generally low, which implies that at low frequencies,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mi>ω</mi>
      <mo class=MathClass-rel><</mo>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>, yielding
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>. Then, Eq. (2) can be rewritten as
<table class=equation>
   <tr>
      <td style="font-size: 15px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>e</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>V</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mi>I</mi>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   which gives the following expression of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>I</mi>
   </math>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Bℓ</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>V</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>b</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   As the output voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>o</mi>
         </mrow>
      </msub>
   </math>
   is measured at the load resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   terminals, we get, by using Eq. (4)
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>o</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>V</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   In addition, the Lorentz force
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
   </math>
   can be rewritten as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>BℓI</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-open>(</mo>
                  <mi>V</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>b</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<p>and integrated in the equation of movement (1)
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mo class=MathClass-bin>−</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>B</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <msup>
                           <mrow>
                              <mi>ℓ</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>l</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-open>(</mo>
            <mi>V</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi>V</mi>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   By adding
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   on both side of the previous equation, we get
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mo class=MathClass-bin>−</mo>
            <mrow>
               <mo form=prefix fence=true>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>B</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <msup>
                           <mrow>
                              <mi>ℓ</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>l</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo form=postfix fence=true>)</mo>
            </mrow>
            <mo class=MathClass-open>(</mo>
            <mi>V</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(8)
</table>
<p>that can be rewritten as
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mo class=MathClass-bin>−</mo>
            <msubsup>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>V</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   where
   <span style="font-size: 14px">
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>B</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   </span>. We notice in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msubsup>
   </math>
   the presence of electrical terms, which correspond to the electrical resistances brought back into the mechanical domain by the electrodynamic coupling. We are now able to get the expression of the sensitivity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
   </math>
   by using Eqs. (5) and (9)
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>o</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>b</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
   </math>
   can be rewritten to get the following expression
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <mfrac>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>B</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   is to the total loss term and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>
   is the natural pulsation from which the natural frequency 
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
         <mrow>
            <mfrac>
                     <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
               <mrow>
                     <mrow>
                        <mn>2</mn><mi>π</mi>
                     </mrow>
               </mrow>
            </mfrac>
         </mrow>
   </math>
 can be calculated. Finally, we get the following “filter” form
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   is the quality factor.
<p>
   The frequency response of a geophone whose parameters are given in <a href="#" data-img-src=${config.ASSETS_URL}/04/13/theory/table-1.svg data-img-alt="Table 1. Parameters of the Longet geophone LGT-20D10.">Table 1</a> is plotted in <a href="#" data-img-alt="Figure 4. Theoretical sensitivity response of the Longet geophone LGT-20D10." data-img-src=${config.ASSETS_URL}/04/13/theory/figure-4.svg>Figure 4</a> for several values of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>. The sensitivity is calculated in dB using
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>dB</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>20</mn>
      <mi class=qopname>log</mi>
      <mo>⁡</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>g</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>re</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>re</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   V
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>⋅</mo>
   </math>
   s/m.
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Parameters of the Longet geophone LGT-20D10.</figcaption>
      <img src=${config.ASSETS_URL}/04/13/theory/table-1.svg alt="Table 1. Parameters of the Longet geophone LGT-20D10.">
   </figure>
</div>
<p>
   One can see that the frequency response has the shape of a second order high-pass filter. The load resistance value can be used to adjust the frequency response: a lower value results in a flatter curve at the expense of lower sensitivity and a higher cut-off frequency.<br class=newline>The nominal sensitivity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   obtained for the plateau part of the response is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   which tends to the intrinsic sensitivity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   for a very large load resistance.
   <figure class=figure>
<p><img alt="Figure 4. Theoretical sensitivity response of the Longet geophone LGT-20D10." src=${config.ASSETS_URL}/04/13/theory/figure-4.svg><figcaption class=caption>Figure 4. Theoretical sensitivity response of the Longet geophone LGT-20D10.</figcaption></figure>
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, the frequency response of an electrodynamic geophone was calculated for different electrical loads. The response has a second order high-pass filter form. The next Lessons will be devoted to electroacoustic transducers.
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
  <dt>2-DOF
  <dd>2 Degrees Of Freedom
  <dt>EMF
  <dd>ElectroMotive Force
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-Sang>[1]
   <dd class=thebibliography id=bib-1>
      <p>Sang, Yongjie and Dubus, Bertrand. “Performance assessment of a small-size ocean wave energy harvester”. In: <span class=cmti-10>MATEC Web Conf. </span>283 (2019).
</dl>`;

export default theory;
